// src/components/ui/InfoModal.tsx
import React from 'react';
import { HelpCircle} from 'lucide-react'; // Keep X import if DialogContent uses it internally, or remove if not needed elsewhere
import {
  Dialog,
  DialogContent,  
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  // Removed DialogClose import as we'll use the default one from DialogContent
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import VideoLink from './VideoLink';
import { VideoSlug } from '@/types/videos';


interface InfoModalProps {
  title: string;
  description: React.ReactNode; // Allow rich text or components
  videoUrl?: string; // Optional URL for embeddable video (e.g., YouTube, Vimeo)
  isVideo?: boolean;
  link?: VideoSlug;
  triggerClassName?: string; // Optional class for positioning/styling the trigger
  modalContentClassName?: string; // Optional class for modal content styling
  open?: boolean; // Optional controlled open state
  onOpenChange?: (open: boolean) => void; // Optional callback for open state changes
}

export const InfoModal: React.FC<InfoModalProps> = ({
  title,
  description,
  videoUrl,
  isVideo,
  link,
  triggerClassName,
  modalContentClassName,
  open,
  onOpenChange,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          type="button" // Prevent form submission if inside a form
          variant="ghost"
          size="icon"
          className={cn('h-5 w-5 text-muted-foreground hover:text-foreground cursor-help p-0', triggerClassName)}
          aria-label={`More info about ${title}`}
        >
          <HelpCircle className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent
        className={cn("sm:max-w-[525px]", modalContentClassName)}
        onOpenAutoFocus={(e) => e.preventDefault()} // Prevent auto-focusing first element
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>

        </DialogHeader>
        <div className="mt-4 space-y-4 text-sm text-muted-foreground">
          {typeof description === 'string' ? <p>{description}</p> : description}
          {isVideo && (
            <div className="aspect-video w-full overflow-hidden rounded-md border">
              <iframe
                width="100%"
                height="100%"
                src={videoUrl} // Basic embed, might need adjustments based on video provider for autoplay, controls etc.
                title={`${title} Video Tutorial`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                ></iframe>
            </div>
          )}
          {link && <VideoLink to={link} />}
        </div>
        {/* No explicit footer needed unless required later */}
      </DialogContent>
    </Dialog>
  );
};

export default InfoModal;